import PropTypes from 'prop-types';

import Box, { BoxProps as MuiBoxProps } from '@mui/material/Box';
import { styled } from '@mui/material/styles';
import {
  AudioIcon,
  Check,
  CycleIcon,
  VideoIcon,
} from '@classity-edtech/ui-base/dist/components/Displays';
import { Radio, Stack, Typography } from '@mui/material';
import { globalColours } from '@classity-edtech/ui-base/dist/style/source';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { useEffect, useRef, useState } from 'react';

export interface HomeProps extends MuiBoxProps {}

export const DeviceChecks = ({ className }: HomeProps) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const audioRef = useRef(null);

  const [camStatus, setCamStatus] = useState(false);
  const [isCameraOn, setIsCameraOn] = useState(false);
  const [isMicOn, setIsMicOn] = useState(false);
  const [screenShareStatus, setScreenShareStatus] = useState(false);
  const [micStatus, setMicStatus] = useState(false);
  const [speakerStatus, setSpeakerStatus] = useState(false);
  const [connectivityStatus, setConnectivityStatus] = useState(false);

  const [status, setStatus] = useState('Testing...');
  const [stream, setStream] = useState(null);

  const startCamera = async () => {
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({ video: true });
      setStream(mediaStream);
      videoRef.current.srcObject = mediaStream;
      await videoRef.current.play();
      setStatus('Granted');
      setIsCameraOn(true);
      setCamStatus(true);
    } catch (err) {
      setCamStatus(false);
      if (err.name === 'NotAllowedError') {
        setStatus('Denied');
      } else {
        setStatus('Error');
        console.error('Camera error:', err);
      }
    }
  };

  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
      setStatus('idle');
      setIsCameraOn(false);
    }
  };

  const testMicrophone = () => {
    if (!audioRef.current) {
      audioRef.current = new Audio(
        'https://static04.classity.co/aud/sunflower-street-drumloop-85bpm-163900.mp3',
      );
    }

    audioRef.current.play().catch(err => {
      console.error('Audio playback failed', err);
    });
  };

  const pauseAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
  };

  const testConnectivity = () => {
    setConnectivityStatus(navigator.onLine);
  };

  const checkMicrophone = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach(track => track.stop());
      return true;
    } catch (error) {
      return false;
    }
  };

  const handleMicCheck = async () => {
    const hasMic = await checkMicrophone();
    setMicStatus(hasMic);
  };

  const checkScreenShare = async () => {
    try {
      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: true,
      });

      stream.getTracks().forEach(track => track.stop());
      return true;
    } catch (err) {
      return false;
    }
  };

  const handleScreenCheck = async () => {
    const canShare = await checkScreenShare();
    setScreenShareStatus(canShare);
  };

  const toggleIsMicOn = () => {
    setIsMicOn(value => !value);
  };

  useEffect(() => {
    setConnectivityStatus(navigator.onLine);
  }, []);

  useEffect(() => {
    return () => {
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
    };
  }, [stream]);

  return (
    <>
      <Stack sx={{ width: { xs: '100%', md: '75%' }, mb: 3 }}>
        <Typography variant='subtitle5'>Online lesson device readiness check</Typography>
        <Typography variant='body2'>
          Ensure a flawless virtual lesson experience by following these critical device readiness
          checks to prevent technical difficulties, audio issues, or video latency.
        </Typography>
      </Stack>

      <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column-reverse', md: 'row' } }}>
        <Stack sx={{ gap: 3 }}>
          <Stack
            sx={{
              height: { xs: '189', md: '362px' },
              aspectRatio: '1.5/1',
              position: 'relative',
              py: 1.5,
              px: 1,
              borderRadius: '8px',
              justifyContent: 'space-between',
            }}
          >
            <video
              ref={videoRef}
              width='320'
              height='240'
              autoPlay
              muted
              style={{
                width: '100%',
                height: '100%',
                position: 'absolute',
                inset: 0,
                borderRadius: '8px',
                filter: 'brightness(70%)',
                backgroundColor: 'black',
                objectFit: 'cover',
              }}
            />

            <Box
              sx={{
                display: 'flex',
                gap: 2,
                justifyContent: 'space-between',
                position: 'relative',
                zIndex: 2,
                color: globalColours['$global-colour-base-contrast'],
                backgroundColor: globalColours['$global-colour-background-overlay'],
                mx: -1,
                my: -1.5,
                px: 1,
                py: 1.5,
              }}
            >
              <Typography variant='body3'>Checking your device readiness...</Typography>
              <Typography variant='body3'>{status}</Typography>
            </Box>

            <Box
              sx={{
                display: 'flex',
                gap: 1,
                position: 'relative',
                zIndex: 2,
                color: globalColours['$global-colour-base-contrast'],
                mx: 'auto',
              }}
            >
              <Box
                sx={{
                  p: 1,
                  minWidth: '75px',
                  display: 'flex',
                  justifyContent: 'center',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  backgroundColor: isMicOn
                    ? globalColours['$global-colour-interactive-accent-active']
                    : '',
                }}
                onClick={toggleIsMicOn}
              >
                <AudioIcon />
              </Box>
              <Box
                sx={{
                  p: 1,
                  minWidth: '75px',
                  display: 'flex',
                  justifyContent: 'center',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  backgroundColor: isCameraOn
                    ? globalColours['$global-colour-interactive-accent-active']
                    : '',
                }}
                onClick={isCameraOn ? stopCamera : startCamera}
              >
                <VideoIcon />
              </Box>
            </Box>
          </Stack>

          <Stack sx={{ gap: 4 }}>
            <Box sx={{ display: 'flex' }}>
              <Stack sx={{ minWidth: '170px' }}>
                <Typography variant='body3SemiBold'>Speaker</Typography>
                <Typography variant='body3'>Can you hear a sound?</Typography>
              </Stack>

              <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    px: 1.5,
                    gap: 1.5,
                    cursor: 'pointer',
                  }}
                >
                  <Radio
                    disableRipple
                    checked={speakerStatus}
                    onChange={() => {
                      setSpeakerStatus(true);
                      pauseAudio();
                    }}
                    sx={{ p: 0 }}
                  />
                  <Typography variant='body3'>Yes</Typography>
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    px: 1.5,
                    gap: 1.5,
                    cursor: 'pointer',
                  }}
                >
                  <Radio
                    disableRipple
                    checked={!speakerStatus}
                    onChange={() => {
                      setSpeakerStatus(false);
                      pauseAudio();
                    }}
                    sx={{ p: 0 }}
                  />
                  <Typography variant='body3'>No</Typography>
                </Box>

                <CycleIcon sx={{ width: '16px', height: '16px' }} onClick={testMicrophone} />
              </Box>
            </Box>
            <Box sx={{ display: 'flex' }}>
              <Stack sx={{ minWidth: '170px' }}>
                <Typography variant='body3SemiBold'>Microphone</Typography>
              </Stack>

              <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', px: 1.5, gap: 1.5 }}>
                  {micStatus ? (
                    <>
                      <Typography variant='body3'>Ready</Typography>
                      <Check sx={{ width: '16px', height: '16px' }} />
                    </>
                  ) : (
                    <Typography variant='body3'>Testing</Typography>
                  )}
                  {!micStatus && (
                    <>
                      <Box
                        sx={{
                          display: 'flex',
                          gap: 0.5,
                        }}
                      >
                        <Box
                          sx={{
                            backgroundColor: globalColours['$global-colour-core-brand-1'],
                            width: '6px',
                            height: '6px',
                            borderRadius: '50%',
                          }}
                        ></Box>
                        <Box
                          sx={{
                            backgroundColor: globalColours['$global-colour-core-brand-1'],
                            opacity: 0.08,
                            width: '6px',
                            height: '6px',
                            borderRadius: '50%',
                          }}
                        ></Box>
                        <Box
                          sx={{
                            backgroundColor: globalColours['$global-colour-core-brand-1'],
                            opacity: 0.08,
                            width: '6px',
                            height: '6px',
                            borderRadius: '50%',
                          }}
                        ></Box>
                      </Box>
                    </>
                  )}

                  <CycleIcon sx={{ width: '16px', height: '16px' }} onClick={handleMicCheck} />
                </Box>
              </Box>
            </Box>
            <Box sx={{ display: 'flex' }}>
              <Stack sx={{ minWidth: '170px' }}>
                <Typography variant='body3SemiBold'>Camera</Typography>
              </Stack>

              <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', px: 1.5, gap: 1.5 }}>
                  {camStatus ? (
                    <>
                      <Typography variant='body3'>Ready</Typography>
                      <Check sx={{ width: '16px', height: '16px' }} />
                    </>
                  ) : (
                    <Typography variant='body3'>Pending</Typography>
                  )}

                  {!camStatus && (
                    <Box
                      sx={{
                        display: 'flex',
                        gap: 0.5,
                      }}
                    >
                      <Box
                        sx={{
                          backgroundColor: globalColours['$global-colour-content-primary'],
                          width: '6px',
                          height: '6px',
                          borderRadius: '50%',
                        }}
                      ></Box>
                      <Box
                        sx={{
                          backgroundColor: globalColours['$global-colour-content-primary'],
                          opacity: 0.08,
                          width: '6px',
                          height: '6px',
                          borderRadius: '50%',
                        }}
                      ></Box>
                      <Box
                        sx={{
                          backgroundColor: globalColours['$global-colour-content-primary'],
                          opacity: 0.08,
                          width: '6px',
                          height: '6px',
                          borderRadius: '50%',
                        }}
                      ></Box>
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>
            <Box sx={{ display: 'flex' }}>
              <Stack sx={{ minWidth: '170px' }}>
                <Typography variant='body3SemiBold'>Screen Share</Typography>
              </Stack>

              <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', px: 1.5, gap: 0.5 }}>
                  {screenShareStatus ? (
                    <>
                      <Typography variant='body3'>Ready</Typography>
                      <Check sx={{ width: '16px', height: '16px' }} />
                    </>
                  ) : (
                    <Typography variant='body3'>Testing</Typography>
                  )}
                </Box>
                <CycleIcon sx={{ width: '16px', height: '16px' }} onClick={handleScreenCheck} />
              </Box>
            </Box>
            <Box sx={{ display: 'flex' }}>
              <Stack sx={{ minWidth: '170px' }}>
                <Typography variant='body3SemiBold'>Connectivity</Typography>
              </Stack>

              <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', px: 1.5, gap: 0.5 }}>
                  {connectivityStatus ? (
                    <>
                      <Typography variant='body3'>Ready</Typography>
                      <Check sx={{ width: '16px', height: '16px' }} />
                    </>
                  ) : (
                    <>
                      <Typography variant='body3'>Failed</Typography>
                      <ErrorOutlineIcon sx={{ width: '16px', height: '16px' }} />
                    </>
                  )}
                </Box>
                {!connectivityStatus && (
                  <Box
                    sx={{ display: 'flex', alignItems: 'center', px: 1.5, gap: 0.5 }}
                    onClick={testConnectivity}
                  >
                    <Typography variant='body3'>Retry</Typography>
                    <CycleIcon sx={{ width: '16px', height: '16px' }} />
                  </Box>
                )}
              </Box>
            </Box>
          </Stack>
        </Stack>
      </Box>
    </>
  );
};

DeviceChecks.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
};

DeviceChecks.defaultProps = {
  children: null,
  className: null,
};

export default styled<any>(DeviceChecks)(
  ({ theme }) => `
`,
);
